import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ContactForm from './ContactForm';
import './ContactModal.css';

const CALENDLY_BASE_URL = 'https://calendly.com/d/cr45-yj3-4tj/bytesand-ai-audit?background_color=1a1a1a&text_color=ffffff&primary_color=d4a574';

const ContactModal = ({ isOpen, onClose }) => {
  const [showCalendly, setShowCalendly] = useState(false);
  const [invitee, setInvitee] = useState({ name: '', email: '' });

  // Prevent background scroll when modal is open
  useEffect(() => {
    const scrollableContainer = document.querySelector('.home-container');
    if (!scrollableContainer) return;

    if (isOpen) {
      scrollableContainer.style.overflowY = 'hidden';
    } else {
      scrollableContainer.style.overflowY = 'scroll';
    }

    return () => {
      if (scrollableContainer) {
        scrollableContainer.style.overflowY = 'scroll';
      }
    };
  }, [isOpen]);

  // Build Calendly URL with prefilled name and email
  const calendlyUrlWithPrefill = `${CALENDLY_BASE_URL}&name=${encodeURIComponent(invitee.name)}&email=${encodeURIComponent(invitee.email)}`;

  // Load Calendly script when needed
  useEffect(() => {
    if (showCalendly && !document.getElementById('calendly-widget-script')) {
      const script = document.createElement('script');
      script.id = 'calendly-widget-script';
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [showCalendly]);

  // Initialize Calendly widget when shown
  useEffect(() => {
    if (showCalendly && window.Calendly) {
      window.Calendly.initInlineWidget({
        url: calendlyUrlWithPrefill,
        parentElement: document.getElementById('calendly-embed'),
      });
    }
  }, [showCalendly, calendlyUrlWithPrefill]);

  // Callback for ContactForm to show Calendly with name and email
  const handleShowCalendly = useCallback((name, email) => {
    setInvitee({ name, email });
    setShowCalendly(true);
  }, []);

  // Close modal resets Calendly state
  const handleClose = () => {
    setShowCalendly(false);
    setInvitee({ name: '', email: '' });
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          {/* Modal Content */}
          <motion.div
            className="modal-container"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div className="modal-header">
              <h2>{showCalendly ? 'Book a Call' : 'Get In Touch'}</h2>
              <button className="modal-close-button" onClick={handleClose}>
                ×
              </button>
            </div>
            <div className="modal-content">
              {showCalendly ? (
                <div id="calendly-embed" style={{ minWidth: 320, height: 700 }} />
              ) : (
                <ContactForm onSuccess={handleShowCalendly} />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContactModal; 